import { supabase, getRedirectUrl } from './supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/** Avoid hung dashboard fetches if auth client never resolves session (rare networking issues). */
const AUTH_HEADER_MS = 12_000;

async function getAuthHeaders() {
  let session;
  try {
    const { data } = await Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('getSession_timeout')), AUTH_HEADER_MS)
      ),
    ]);
    session = data?.session;
  } catch {
    session = null;
  }
  const headers = {};
  if (SUPABASE_ANON_KEY) {
    headers.apikey = SUPABASE_ANON_KEY;
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  return headers;
}

async function edgeFn(name, path = '', options = {}) {
  const headers = await getAuthHeaders();
  const url = `${SUPABASE_URL}/functions/v1/${name}${path}`;
  const resp = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...(options.headers || {}),
    },
  });
  let data;
  try {
    data = await resp.json();
  } catch {
    throw new Error(`Edge function error: ${resp.status} (invalid JSON body)`);
  }
  if (!resp.ok) throw new Error(data?.error || `Edge function error: ${resp.status}`);
  return { data, status: resp.status };
}

/**
 * Resolve DB profile + first workspace (same ordering as AuthContext).
 * Avoids `.single()` + null profile crashes when auth exists but `users` row is missing (race / RLS).
 */
async function getProfileWorkspaceContext() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { user: null, profile: null, workspaceId: null };
  const { data: profile } = await supabase
    .from('users')
    .select('user_id')
    .eq('auth_id', user.id)
    .maybeSingle();
  if (!profile?.user_id) return { user, profile: null, workspaceId: null };
  const { data: memberships } = await supabase
    .from('workspace_members')
    .select('workspace_id')
    .eq('user_id', profile.user_id);
  const sorted = [...(memberships || [])].sort((a, b) =>
    String(a.workspace_id).localeCompare(String(b.workspace_id))
  );
  const workspaceId = sorted[0]?.workspace_id ?? null;
  return { user, profile, workspaceId };
}

const api = {
  // Auth - now handled by Supabase Auth directly
  get: async (path, options) => {
    if (path === '/auth/me') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data } = await supabase.from('users').select('*').eq('auth_id', user.id).maybeSingle();
      if (!data) throw new Error('Profile not found');
      return { data };
    }
    if (path === '/workspaces') {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data: profile } = await supabase.from('users').select('user_id').eq('auth_id', user.id).maybeSingle();
      if (!profile?.user_id) throw new Error('Profile not found');
      const { data: memberships } = await supabase.from('workspace_members').select('workspace_id').eq('user_id', profile.user_id);
      const wsIds = (memberships || []).map(m => m.workspace_id);
      const { data } = await supabase.from('workspaces').select('*').in('workspace_id', wsIds);
      return { data };
    }

    // CAPI settings
    if (path === '/capi/settings') {
      return edgeFn('capi', '/settings');
    }

    // Analytics endpoints go through edge functions
    if (path.startsWith('/analytics/')) {
      const parts = path.replace('/analytics/', '');
      const params = options?.params || {};
      const qs = new URLSearchParams(params).toString();
      return edgeFn('analytics', `/${parts}${qs ? '?' + qs : ''}`);
    }

    // Billing status
    if (path.startsWith('/billing/')) {
      const parts = path.replace('/billing/', '');
      const qs = path.includes('?') ? '' : '';
      return edgeFn('billing', `/${parts}`);
    }

    // Links
    if (path.match(/^\/links\/[^/]+\/ab-test\/stats$/)) {
      const linkId = path.split('/')[2];
      return edgeFn('analytics', `/link/${linkId}/ab-stats`);
    }

    // Alerts
    if (path.match(/^\/links\/[^/]+\/alerts$/)) {
      const linkId = path.split('/')[2];
      return edgeFn('alerts', `?link_id=${linkId}`);
    }
    // Health history
    if (path.match(/^\/links\/[^/]+\/health\/history$/)) {
      const linkId = path.split('/')[2];
      return edgeFn('link-health', `/history?link_id=${linkId}&limit=20`);
    }
    // Health manual check (single)
    if (path.match(/^\/links\/[^/]+\/health$/)) {
      const linkId = path.split('/')[2];
      return edgeFn('link-health', `?link_id=${linkId}`);
    }
    // Import history
    if (path === '/links/import/history') {
      return edgeFn('import', '');
    }
    // Webhook deliveries
    if (path.match(/\/webhooks\/deliveries$/)) {
      return edgeFn('webhooks', '/deliveries');
    }

    if (path.match(/^\/links\/[^/]+\/export$/)) {
      const linkId = path.split('/')[2];
      const { data: clicks } = await supabase.from('clicks').select('*').eq('link_id', linkId).eq('is_bot', false).order('timestamp', { ascending: false });
      const rows = (clicks || []).map(c => [c.timestamp, c.country, c.city, c.device_type, c.os, c.browser, c.referrer, c.source_type, c.utm_source, c.utm_medium, c.utm_campaign].join(','));
      const csv = ['timestamp,country,city,device,os,browser,referrer,source_type,utm_source,utm_medium,utm_campaign', ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      return { data: blob };
    }

    if (path.match(/^\/links\/[^/]+$/)) {
      const linkId = path.replace('/links/', '');
      const { data, error } = await supabase.from('links').select('*').eq('link_id', linkId).single();
      if (error) throw error;
      return { data };
    }
    if (path === '/links') {
      const params = options?.params || {};
      const { user, profile, workspaceId } = await getProfileWorkspaceContext();
      if (!user) throw new Error('Not authenticated');
      if (!profile?.user_id || !workspaceId) return { data: { links: [], total: 0, page: 1, limit: 20 } };

      let query = supabase.from('links').select('*', { count: 'exact' }).eq('workspace_id', workspaceId);

      if (params.search) {
        query = query.or(`slug.ilike.%${params.search}%,destination_url.ilike.%${params.search}%,title.ilike.%${params.search}%`);
      }
      if (params.tag) query = query.contains('tags', [params.tag]);
      if (params.status) query = query.eq('status', params.status);

      const sortField = ['created_at', 'total_clicks', 'last_clicked'].includes(params.sort_by) ? params.sort_by : 'created_at';
      const ascending = params.sort_order === 'asc';
      query = query.order(sortField, { ascending });

      const page = params.page || 1;
      const limit = params.limit || 20;
      const from = (page - 1) * limit;
      query = query.range(from, from + limit - 1);

      const { data: links, count, error } = await query;
      if (error) throw error;
      return { data: { links: links || [], total: count || 0, page, limit } };
    }

    // API Keys
    if (path === '/api-keys') {
      return edgeFn('api', '/v1/keys');
    }

    // Audit log
    if (path.startsWith('/audit')) {
      const qs = path.includes('?') ? path.slice(path.indexOf('?')) : '';
      return edgeFn('api', `/v1/audit${qs}`);
    }

    // Workspace sub-resources
    const pixelMatch = path.match(/\/workspaces\/([^/]+)\/pixels/);
    if (pixelMatch) {
      const { data } = await supabase.from('retargeting_pixels').select('*').eq('workspace_id', pixelMatch[1]);
      return { data };
    }
    const utmMatch = path.match(/\/workspaces\/([^/]+)\/utm-presets/);
    if (utmMatch) {
      const { data } = await supabase.from('utm_presets').select('*').eq('workspace_id', utmMatch[1]);
      return { data };
    }
    const ctaMatch = path.match(/\/workspaces\/([^/]+)\/ctas$/);
    if (ctaMatch) {
      const { data } = await supabase.from('cta_overlays').select('*').eq('workspace_id', ctaMatch[1]);
      return { data };
    }
    const ctaStatsMatch = path.match(/\/workspaces\/([^/]+)\/cta-stats/);
    if (ctaStatsMatch) {
      return edgeFn('analytics', `/workspace/${ctaStatsMatch[1]}/cta-stats`);
    }
    const domainMatch = path.match(/\/workspaces\/([^/]+)\/domains/);
    if (domainMatch) {
      return edgeFn('domains', '');
    }
    const webhookMatch = path.match(/\/workspaces\/([^/]+)\/webhooks/);
    if (webhookMatch) {
      return edgeFn('webhooks', '');
    }

    // Folders (B3)
    const folderMatch = path.match(/\/workspaces\/([^/]+)\/folders/);
    if (folderMatch) {
      const { data } = await supabase.from('folders').select('*').eq('workspace_id', folderMatch[1]).order('created_at');
      return { data };
    }

    // Tags (B10)
    const tagMatch = path.match(/\/workspaces\/([^/]+)\/tags/);
    if (tagMatch) {
      const { data } = await supabase.from('tags').select('*').eq('workspace_id', tagMatch[1]).order('name');
      return { data };
    }

    // Conversions (B5)
    const convMatch = path.match(/\/workspaces\/([^/]+)\/conversions/);
    if (convMatch) {
      const { data } = await supabase.from('conversions').select('*').eq('workspace_id', convMatch[1]).order('created_at', { ascending: false }).limit(100);
      return { data };
    }

    // Customers (B5)
    const custMatch = path.match(/\/workspaces\/([^/]+)\/customers/);
    if (custMatch) {
      const { data } = await supabase.from('customers').select('*').eq('workspace_id', custMatch[1]).order('last_seen', { ascending: false }).limit(100);
      return { data };
    }

    throw new Error(`Unknown GET path: ${path}`);
  },

  post: async (path, body) => {
    if (path === '/auth/logout') {
      await supabase.auth.signOut();
      return { data: { message: 'Logged out' } };
    }
    if (path === '/auth/session') {
      // Legacy Emergent auth - not used with Supabase Auth
      throw new Error('Use Supabase Auth instead of session exchange');
    }

    // Links
    if (path === '/links') {
      const { user, profile, workspaceId } = await getProfileWorkspaceContext();
      if (!user) throw new Error('Not authenticated');
      if (!profile?.user_id) {
        throw new Error('Account setup is still completing. Wait a moment and try again, or sign out and finish onboarding.');
      }
      if (!workspaceId) throw new Error('No workspace yet. Finish onboarding to create links.');

      const linkId = `lnk_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
      let slug = body.slug?.trim();
      if (!slug) {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        slug = Array.from({ length: 7 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      }

      const { data, error } = await supabase.from('links').insert({
        link_id: linkId,
        workspace_id: workspaceId,
        created_by: profile.user_id,
        destination_url: body.destination_url,
        slug,
        title: body.title || '',
        tags: body.tags || [],
        utm_params: body.utm_params || {},
        status: 'active',
        expiry_date: body.expiry_date || null,
        max_clicks: body.max_clicks || null,
        password_hash: null,
        retargeting_pixels: body.retargeting_pixels || [],
        routing_rules: body.routing_rules || [],
        // New fields (B7, B8, B9, B4, F5)
        comments: body.comments || '',
        cloaked: body.cloaked || false,
        indexable: body.indexable !== false,
        conversion_tracking: body.conversion_tracking || false,
        og_title: body.og_title || null,
        og_description: body.og_description || null,
        og_image: body.og_image || null,
        og_overrides: body.og_overrides || null,
        domain: body.domain || null,
        short_domain: body.short_domain || null,
        folder_id: body.folder_id || null,
        cta_overlay_id: body.cta_overlay_id || null,
      }).select().single();
      if (error) throw error;
      return { data, status: 201 };
    }

    if (path === '/links/check-slug') {
      const { data } = await supabase.from('links').select('link_id').eq('slug', body.slug).maybeSingle();
      return { data: { available: !data } };
    }

    if (path === '/links/bulk-delete') {
      const { user, profile, workspaceId } = await getProfileWorkspaceContext();
      if (!user) throw new Error('Not authenticated');
      if (!profile?.user_id || !workspaceId) throw new Error('No workspace');
      const { count } = await supabase.from('links').delete({ count: 'exact' }).in('link_id', body.link_ids).eq('workspace_id', workspaceId);
      return { data: { deleted: count || 0 } };
    }

    // Alerts
    if (path.match(/^\/links\/[^/]+\/alerts$/)) {
      const linkId = path.split('/')[2];
      return edgeFn('alerts', '', { method: 'POST', body: JSON.stringify({ ...body, link_id: linkId }) });
    }

    // Bulk import
    if (path === '/links/import') {
      return edgeFn('import', '', { method: 'POST', body: JSON.stringify(body) });
    }

    // A/B test endpoints
    const abSetupMatch = path.match(/\/links\/([^/]+)\/ab-test$/);
    if (abSetupMatch && !path.includes('declare-winner')) {
      const linkId = abSetupMatch[1];
      const variants = body.variants.map((v, i) => ({ index: i, url: v.url, weight: v.weight, clicks: 0 }));
      const abTest = { enabled: true, variants, winner_index: null, created_at: new Date().toISOString() };
      const { data } = await supabase.from('links').update({ ab_test: abTest }).eq('link_id', linkId).select().single();
      return { data, status: 201 };
    }

    const abWinnerMatch = path.match(/\/links\/([^/]+)\/ab-test\/declare-winner/);
    if (abWinnerMatch) {
      const linkId = abWinnerMatch[1];
      const { data: link } = await supabase.from('links').select('ab_test').eq('link_id', linkId).single();
      if (!link?.ab_test) throw new Error('No A/B test found');
      const winUrl = link.ab_test.variants[body.variant_index || body.winner_index]?.url;
      const { data } = await supabase.from('links').update({
        ab_test: { ...link.ab_test, winner_index: body.variant_index ?? body.winner_index, enabled: false },
        destination_url: winUrl,
      }).eq('link_id', linkId).select().single();
      return { data };
    }

    // API Keys
    if (path === '/api-keys') {
      return edgeFn('api', '/v1/keys', { method: 'POST', body: JSON.stringify(body) });
    }

    // AI
    if (path.startsWith('/ai/')) {
      const action = path.replace('/ai/', '');
      return edgeFn('ai', `/${action}`, { method: 'POST', body: JSON.stringify(body) });
    }

    // CAPI
    if (path === '/capi/test') {
      return edgeFn('capi', '/test', { method: 'POST', body: JSON.stringify(body) });
    }
    if (path === '/capi/event') {
      return edgeFn('capi', '/event', { method: 'POST', body: JSON.stringify(body) });
    }

    // Billing
    if (path.startsWith('/billing/')) {
      const action = path.replace('/billing/', '');
      return edgeFn('billing', `/${action}`, { method: 'POST', body: JSON.stringify(body) });
    }

    // Workspace sub-resources
    const pixelCreateMatch = path.match(/\/workspaces\/([^/]+)\/pixels/);
    if (pixelCreateMatch) {
      const pixelId = `px_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
      const { data, error } = await supabase.from('retargeting_pixels').insert({
        pixel_id: pixelId, workspace_id: pixelCreateMatch[1], ...body,
      }).select().single();
      if (error) throw error;
      return { data, status: 201 };
    }
    const utmCreateMatch = path.match(/\/workspaces\/([^/]+)\/utm-presets/);
    if (utmCreateMatch) {
      const presetId = `utm_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
      const { data, error } = await supabase.from('utm_presets').insert({
        preset_id: presetId, workspace_id: utmCreateMatch[1], ...body,
      }).select().single();
      if (error) throw error;
      return { data, status: 201 };
    }
    const ctaCreateMatch = path.match(/\/workspaces\/([^/]+)\/ctas/);
    if (ctaCreateMatch) {
      const ctaId = `cta_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
      const { style: bodyStyle, ...bodyWithoutStyle } = body;
      const style = bodyStyle || { bg_color: '#0F1020', text_color: '#FFFFFF', button_color: '#5B47FB', button_text_color: '#FFFFFF' };
      const { data, error } = await supabase.from('cta_overlays').insert({
        cta_id: ctaId, workspace_id: ctaCreateMatch[1], style, ...bodyWithoutStyle,
      }).select().single();
      if (error) throw error;
      return { data, status: 201 };
    }
    const webhookCreateMatch = path.match(/\/workspaces\/([^/]+)\/webhooks/);
    if (webhookCreateMatch) {
      return edgeFn('webhooks', '', { method: 'POST', body: JSON.stringify(body) });
    }
    const domainCreateMatch = path.match(/\/workspaces\/([^/]+)\/domains/);
    if (domainCreateMatch) {
      return edgeFn('domains', '', { method: 'POST', body: JSON.stringify(body) });
    }

    // Domain verify
    const domainVerifyMatch = path.match(/\/workspaces\/([^/]+)\/domains\/([^/]+)\/verify/);
    if (domainVerifyMatch) {
      return edgeFn('domains', `/${domainVerifyMatch[2]}/verify`, { method: 'POST' });
    }

    // Webhook test
    const webhookTestMatch = path.match(/\/workspaces\/([^/]+)\/webhooks\/([^/]+)\/test/);
    if (webhookTestMatch) {
      return edgeFn('webhooks', `/${webhookTestMatch[2]}/test`, { method: 'POST' });
    }

    // Conversions track (B5) — public endpoint
    if (path === '/track') {
      const convId = `conv_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
      const { data, error } = await supabase.from('conversions').insert({
        conversion_id: convId,
        workspace_id: body.workspace_id,
        link_id: body.link_id || null,
        event_name: body.event_name || 'conversion',
        value: body.value || null,
        currency: body.currency || 'USD',
        metadata: body.metadata || {},
        created_at: new Date().toISOString(),
      }).select().single();
      if (error) throw error;
      return { data, status: 201 };
    }

    throw new Error(`Unknown POST path: ${path}`);
  },

  put: async (path, body) => {
    // CAPI settings
    if (path === '/capi/settings') {
      return edgeFn('capi', '/settings', { method: 'PUT', body: JSON.stringify(body) });
    }

    // Links
    const linkUpdateMatch = path.match(/\/links\/([^/]+)$/);
    if (linkUpdateMatch) {
      const linkId = linkUpdateMatch[1];
      const updates = { ...body };
      if (updates.utm_params) updates.utm_params = updates.utm_params;
      delete updates.slug;
      const { data, error } = await supabase.from('links').update(updates).eq('link_id', linkId).select().single();
      if (error) throw error;
      return { data };
    }

    // Routing rules
    const routingMatch = path.match(/\/links\/([^/]+)\/routing-rules/);
    if (routingMatch) {
      const { data, error } = await supabase.from('links').update({ routing_rules: body.routing_rules || [] }).eq('link_id', routingMatch[1]).select().single();
      if (error) throw error;
      return { data };
    }

    // A/B test update
    const abUpdateMatch = path.match(/\/links\/([^/]+)\/ab-test/);
    if (abUpdateMatch) {
      const linkId = abUpdateMatch[1];
      const { data: link } = await supabase.from('links').select('ab_test').eq('link_id', linkId).single();
      if (!link?.ab_test) throw new Error('No A/B test found');
      const ab = { ...link.ab_test };
      if (body.enabled !== undefined) ab.enabled = body.enabled;
      if (body.variants) {
        for (const v of body.variants) {
          if (v.index < ab.variants.length) {
            ab.variants[v.index].weight = v.weight;
            if (v.url) ab.variants[v.index].url = v.url;
          }
        }
      }
      const { data } = await supabase.from('links').update({ ab_test: ab }).eq('link_id', linkId).select().single();
      return { data };
    }

    // Workspace update
    const wsUpdateMatch = path.match(/\/workspaces\/([^/]+)$/);
    if (wsUpdateMatch) {
      const wsId = wsUpdateMatch[1];
      const { data: ws } = await supabase.from('workspaces').select('settings').eq('workspace_id', wsId).single();
      const updates = {};
      if (body.name) updates.name = body.name;
      if (body.settings) updates.settings = { ...(ws?.settings || {}), ...body.settings };
      const { data, error } = await supabase.from('workspaces').update(updates).eq('workspace_id', wsId).select().single();
      if (error) throw error;
      return { data };
    }

    // Pixel update
    const pixelUpdateMatch = path.match(/\/workspaces\/([^/]+)\/pixels\/([^/]+)/);
    if (pixelUpdateMatch) {
      const { data, error } = await supabase.from('retargeting_pixels').update(body).eq('pixel_id', pixelUpdateMatch[2]).eq('workspace_id', pixelUpdateMatch[1]).select().single();
      if (error) throw error;
      return { data };
    }

    // UTM preset update
    const utmUpdateMatch = path.match(/\/workspaces\/([^/]+)\/utm-presets\/([^/]+)/);
    if (utmUpdateMatch) {
      const { data, error } = await supabase.from('utm_presets').update(body).eq('preset_id', utmUpdateMatch[2]).eq('workspace_id', utmUpdateMatch[1]).select().single();
      if (error) throw error;
      return { data };
    }

    // CTA update
    const ctaUpdateMatch = path.match(/\/workspaces\/([^/]+)\/ctas\/([^/]+)/);
    if (ctaUpdateMatch) {
      const { data, error } = await supabase.from('cta_overlays').update(body).eq('cta_id', ctaUpdateMatch[2]).eq('workspace_id', ctaUpdateMatch[1]).select().single();
      if (error) throw error;
      return { data };
    }

    throw new Error(`Unknown PUT path: ${path}`);
  },

  delete: async (path) => {
    // DELETE /links/{linkId}/alerts/{ruleId}
    const alertDeleteMatch = path.match(/^\/links\/([^/]+)\/alerts\/([^/]+)$/);
    if (alertDeleteMatch) {
      const ruleId = alertDeleteMatch[2];
      return edgeFn('alerts', `/${ruleId}`, { method: 'DELETE' });
    }

    const linkDeleteMatch = path.match(/\/links\/([^/]+)\/ab-test/);
    if (linkDeleteMatch) {
      const linkId = linkDeleteMatch[1];
      await supabase.from('ab_assignments').delete().eq('link_id', linkId);
      const { data } = await supabase.from('links').update({ ab_test: null }).eq('link_id', linkId).select().single();
      return { data };
    }

    // API Keys revoke
    const apiKeyMatch = path.match(/^\/api-keys\/([^/]+)$/);
    if (apiKeyMatch) {
      return edgeFn('api', `/v1/keys/${apiKeyMatch[1]}`, { method: 'DELETE' });
    }

    const singleLinkMatch = path.match(/\/links\/([^/]+)$/);
    if (singleLinkMatch) {
      await supabase.from('links').delete().eq('link_id', singleLinkMatch[1]);
      return { data: { message: 'Link deleted' } };
    }

    const pixelDeleteMatch = path.match(/\/workspaces\/([^/]+)\/pixels\/([^/]+)/);
    if (pixelDeleteMatch) {
      await supabase.from('retargeting_pixels').delete().eq('pixel_id', pixelDeleteMatch[2]).eq('workspace_id', pixelDeleteMatch[1]);
      return { data: { message: 'Pixel deleted' } };
    }

    const utmDeleteMatch = path.match(/\/workspaces\/([^/]+)\/utm-presets\/([^/]+)/);
    if (utmDeleteMatch) {
      await supabase.from('utm_presets').delete().eq('preset_id', utmDeleteMatch[2]).eq('workspace_id', utmDeleteMatch[1]);
      return { data: { message: 'Preset deleted' } };
    }

    const ctaDeleteMatch = path.match(/\/workspaces\/([^/]+)\/ctas\/([^/]+)/);
    if (ctaDeleteMatch) {
      await supabase.from('cta_overlays').delete().eq('cta_id', ctaDeleteMatch[2]).eq('workspace_id', ctaDeleteMatch[1]);
      return { data: { message: 'CTA deleted' } };
    }

    const webhookDeleteMatch = path.match(/\/workspaces\/([^/]+)\/webhooks\/([^/]+)/);
    if (webhookDeleteMatch) {
      return edgeFn('webhooks', `/${webhookDeleteMatch[2]}`, { method: 'DELETE' });
    }

    const domainDeleteMatch = path.match(/\/workspaces\/([^/]+)\/domains\/([^/]+)/);
    if (domainDeleteMatch) {
      return edgeFn('domains', `/${domainDeleteMatch[2]}`, { method: 'DELETE' });
    }

    throw new Error(`Unknown DELETE path: ${path}`);
  },
};

/**
 * Build the public short URL for a link.
 * If the link has a short_domain (e.g. "lfo.to") use that.
 * Otherwise fall back to the legacy Supabase edge function URL.
 */
export const getShortUrl = (slugOrLink, domain) => {
  // Called with a link object: getShortUrl(link)
  if (slugOrLink && typeof slugOrLink === 'object') {
    const { slug, short_domain } = slugOrLink;
    if (short_domain) return `https://${short_domain}/${slug}`;
    return getRedirectUrl(slug);
  }
  // Called with (slug, domain) or just (slug)
  if (domain) return `https://${domain}/${slugOrLink}`;
  return getRedirectUrl(slugOrLink);
};

/**
 * Fetch the platform-provided short domains (lfo.to, gfo.to, cto.to).
 * Returns [{domain, label, sort_order}] sorted by sort_order asc.
 */
export const getPlatformDomains = async () => {
  const { supabase } = await import('./supabase');
  const { data } = await supabase
    .from('platform_domains')
    .select('domain, label, sort_order')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  return data || [];
};

export const abTestApi = {
  create: (linkId, data) => api.post(`/links/${linkId}/ab-test`, data),
  getStats: (linkId) => api.get(`/links/${linkId}/ab-test/stats`),
  declareWinner: (linkId, variantIndex) =>
    api.post(`/links/${linkId}/ab-test/declare-winner`, { variant_index: variantIndex }),
  remove: (linkId) => api.delete(`/links/${linkId}/ab-test`),
};

export default api;
